import { Injectable } from '@angular/core';

export interface CachedPassport {
  passportID: number;
  data: any;
  timestamp: number;
}

export interface CachedComments {
  passportID: number;
  comments: any[];
  timestamp: number;
}

export interface CachedHistory {
  passportID: number;
  history: any[];
  timestamp: number;
}

export interface CachedAuthorisedUsers {
  userId: number;
  authorisedUsers: any[];
  timestamp: number;
}

export interface CachedCommentNotifications {
  userId: number;
  notifications: any[];
  timestamp: number;
}

export interface CachedOrigins {
  key: string; // Composite key: `${roleId}-${passportId || ''}`
  roleId: number;
  passportId?: string; // Optional passport ID
  origins: any[];
  timestamp: number;
}

export interface CachedDraft {
  passportId: string;
  data: any; // PassportDetailsModel with File objects converted to Blobs
  timestamp: number;
}

export interface CachedOutgoingPassport {
  passportId: string;
  data: any; // FormData or submission data
  timestamp: number;
}

@Injectable({
  providedIn: 'root'
})
export class OfflineStorageService {
  private readonly dbName = 'PassportOfflineDB';
  private readonly dbVersion = 7; // Incremented: removed images + shuntingYards stores
  private readonly allStoreNames = [
    'passports', 'comments', 'history', 'metadata',
    'authorisedUsers', 'commentNotifications',
    'origins', 'drafts', 'outgoingPassports',
  ] as const;
  private readonly personaStoreNames = [
    'passports', 'comments', 'history',
    'origins', 'authorisedUsers',
  ] as const;
  private db: IDBDatabase | null = null;
  private initPromise: Promise<void> | null = null;
  private isInitializing = false;

  constructor() {
    // DB will be lazily initialized on first use via ensureDB()
    if (globalThis.window !== undefined) {
      this.setupResumeReconnect();
    }
  }

  /**
   * Re-open IndexedDB after iOS Safari resumes a backgrounded tab.
   */
  private setupResumeReconnect(): void {
    const reconnectIfNeeded = (): void => {
      if (document.visibilityState !== 'visible' || !this.db) {
        return;
      }
      if (this.isConnectionValid()) {
        return;
      }
      console.warn('IndexedDB stale after resume — reinitializing');
      this.resetConnectionState();
      this.initialize();
    };

    document.addEventListener('visibilitychange', reconnectIfNeeded);
    globalThis.window.addEventListener('pageshow', reconnectIfNeeded);
  }

  private resetConnectionState(): void {
    if (this.db) {
      try {
        this.db.close();
      } catch {
        // Connection may already be closed on iOS
      }
    }
    this.db = null;
    this.initPromise = null;
    this.isInitializing = false;
  }

  /**
   * Eagerly initialize the database
   * Optional - DB will auto-initialize on first use if not called
   */
  initialize(): void {
    this.initDB().catch(err => {
      console.warn('Failed to eagerly initialize IndexedDB:', err);
    });
  }

  private async initDB(): Promise<void> {
    // If already initializing, wait for that promise
    if (this.initPromise) {
      return await this.initPromise;
    }

    // If already initialized and connection is valid, return immediately
    if (this.db) {
      try {
        // Quick check if connection is still valid
        const _ = this.db.objectStoreNames;
        return;
      } catch {
        // Connection invalid, reset and reinitialize
        this.db = null;
      }
    }

    this.isInitializing = true;
    this.initPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onerror = () => {
        console.warn('Failed to open IndexedDB:', request.error);
        this.isInitializing = false;
        this.initPromise = null;
        reject(new Error(`Failed to open IndexedDB: ${request.error}`));
      };

      request.onsuccess = () => {
        this.db = request.result;
        
        // Set up connection event listeners
        this.db.onclose = () => {
          console.warn('IndexedDB connection closed unexpectedly');
          this.db = null;
          this.initPromise = null;
          this.isInitializing = false;
        };

        this.db.onerror = (event) => {
          console.warn('IndexedDB error:', event);
        };

        this.db.onversionchange = () => {
          console.warn('IndexedDB version change detected - closing connection');
          if (this.db) {
            this.db.close();
            this.db = null;
            this.initPromise = null;
            this.isInitializing = false;
          }
        };

        console.log('IndexedDB initialized successfully');
        this.isInitializing = false;
        this.initPromise = null;
        resolve();
      };

      request.onupgradeneeded = (event: any) => {
        const db = event.target.result as IDBDatabase;

        // Create object stores if they don't exist
        if (!db.objectStoreNames.contains('passports')) {
          const passportStore = db.createObjectStore('passports', { keyPath: 'passportID' });
          passportStore.createIndex('timestamp', 'timestamp', { unique: false });
          console.log('Created passports object store');
        }

        if (!db.objectStoreNames.contains('comments')) {
          const commentsStore = db.createObjectStore('comments', { keyPath: 'passportID' });
          commentsStore.createIndex('timestamp', 'timestamp', { unique: false });
          console.log('Created comments object store');
        }

        if (!db.objectStoreNames.contains('history')) {
          const historyStore = db.createObjectStore('history', { keyPath: 'passportID' });
          historyStore.createIndex('timestamp', 'timestamp', { unique: false });
          console.log('Created history object store');
        }

        // v7: Delete deprecated images and shuntingYards stores
        if (db.objectStoreNames.contains('images')) {
          db.deleteObjectStore('images');
          console.log('Deleted deprecated images object store');
        }
        if (db.objectStoreNames.contains('shuntingYards')) {
          db.deleteObjectStore('shuntingYards');
          console.log('Deleted deprecated shuntingYards object store');
        }

        if (!db.objectStoreNames.contains('metadata')) {
          db.createObjectStore('metadata', { keyPath: 'key' });
          console.log('Created metadata object store');
        }

        if (!db.objectStoreNames.contains('authorisedUsers')) {
          const authorisedUsersStore = db.createObjectStore('authorisedUsers', { keyPath: 'userId' });
          authorisedUsersStore.createIndex('timestamp', 'timestamp', { unique: false });
          console.log('Created authorisedUsers object store');
        }

        if (!db.objectStoreNames.contains('commentNotifications')) {
          const commentNotificationsStore = db.createObjectStore('commentNotifications', { keyPath: 'userId' });
          commentNotificationsStore.createIndex('timestamp', 'timestamp', { unique: false });
          console.log('Created commentNotifications object store');
        }

        if (!db.objectStoreNames.contains('drafts')) {
          const draftsStore = db.createObjectStore('drafts', { keyPath: 'passportId' });
          draftsStore.createIndex('timestamp', 'timestamp', { unique: false });
          console.log('Created drafts object store');
        }

        if (!db.objectStoreNames.contains('outgoingPassports')) {
          const outgoingPassportsStore = db.createObjectStore('outgoingPassports', { keyPath: 'passportId' });
          outgoingPassportsStore.createIndex('timestamp', 'timestamp', { unique: false });
          console.log('Created outgoingPassports object store');
        }

        if (!db.objectStoreNames.contains('origins')) {
          // Use a composite key: roleId-passportId (or just roleId if no passportId)
          const originsStore = db.createObjectStore('origins', { keyPath: 'key' });
          originsStore.createIndex('roleId', 'roleId', { unique: false });
          originsStore.createIndex('passportId', 'passportId', { unique: false });
          originsStore.createIndex('timestamp', 'timestamp', { unique: false });
          console.log('Created origins object store');
        }
      };
    });
  }

  private async ensureDB(): Promise<IDBDatabase> {
    // Retry logic with exponential backoff
    const maxRetries = 3;
    let lastError: Error | null = null;

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        await this.waitForInitializationIfNeeded();
        await this.initializeIfNeeded();
        
        if (!this.db) {
          throw new Error('Failed to initialize IndexedDB');
        }

        if (this.isConnectionValid()) {
          return this.db;
        }

        // Connection invalid, reset and retry
        this.handleInvalidConnection(attempt, maxRetries);
        lastError = new Error('IndexedDB connection invalid');
      } catch (error: unknown) {
        lastError = error instanceof Error ? error : new Error(String(error));
        await this.waitBeforeRetry(attempt, maxRetries);
      }
    }

    // All retries failed
    throw new Error(`Failed to ensure IndexedDB connection after ${maxRetries} attempts: ${lastError?.message || 'Unknown error'}`);
  }

  /**
   * Wait for initialization if it's in progress
   */
  private async waitForInitializationIfNeeded(): Promise<void> {
    if (this.isInitializing && this.initPromise) {
      await this.initPromise;
    }
  }

  /**
   * Initialize database if not already initialized
   */
  private async initializeIfNeeded(): Promise<void> {
    if (!this.db) {
      await this.initDB();
    }
  }

  /**
   * Check if database connection is still valid
   */
  private isConnectionValid(): boolean {
    try {
      // Try to access objectStoreNames to verify connection is open
      const _ = this.db?.objectStoreNames;
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Handle invalid connection by resetting state
   */
  private handleInvalidConnection(attempt: number, maxRetries: number): void {
    console.warn(`IndexedDB connection invalid (attempt ${attempt + 1}/${maxRetries}), reinitializing...`);
    this.resetConnectionState();
  }

  /**
   * Delete the entire database — more reliable than clear() on iOS WebKit.
   */
  async deleteDatabase(): Promise<void> {
    this.resetConnectionState();

    return new Promise((resolve) => {
      const request = indexedDB.deleteDatabase(this.dbName);

      request.onsuccess = () => {
        console.log('IndexedDB database deleted');
        resolve();
      };
      request.onerror = () => {
        console.warn('deleteDatabase failed:', request.error);
        resolve();
      };
      request.onblocked = () => {
        console.warn('deleteDatabase blocked — another tab may hold the DB open');
        resolve();
      };
    });
  }

  private clearSingleStore(db: IDBDatabase, storeName: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(storeName, 'readwrite');
      const request = transaction.objectStore(storeName).clear();

      transaction.onerror = () => {
        reject(transaction.error ?? new Error(`Transaction failed for ${storeName}`));
      };
      transaction.onabort = () => {
        reject(transaction.error ?? new Error(`Transaction aborted for ${storeName}`));
      };
      request.onsuccess = () => resolve();
      request.onerror = () => {
        reject(request.error ?? new Error(`Clear failed for ${storeName}`));
      };
    });
  }

  private async clearStoresInSequence(
    storeNames: readonly string[],
    options: { continueOnError?: boolean } = {},
  ): Promise<void> {
    const db = await this.ensureDB();
    const continueOnError = options.continueOnError ?? false;
    let hadFailure = false;

    for (const storeName of storeNames) {
      try {
        await this.clearSingleStore(db, storeName);
      } catch (error) {
        hadFailure = true;
        console.warn(`Failed to clear store ${storeName}:`, error);
        if (!continueOnError) {
          throw error;
        }
      }
    }

    if (!hadFailure) {
      console.log(`Cleared IndexedDB stores: ${storeNames.join(', ')}`);
    }
  }

  /**
   * Wait before retrying with exponential backoff
   */
  private async waitBeforeRetry(attempt: number, maxRetries: number): Promise<void> {
    if (attempt < maxRetries - 1) {
      await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 100));
    }
  }

  // ==================== PASSPORT OPERATIONS ====================

  async savePassport(passportID: number, data: any): Promise<void> {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['passports'], 'readwrite');
      const store = transaction.objectStore('passports');
      
      const cachedPassport: CachedPassport = {
        passportID,
        data,
        timestamp: Date.now()
      };

      const request = store.put(cachedPassport);

      request.onsuccess = () => {
        console.log(`Saved passport ${passportID} to IndexedDB`);
        resolve();
      };

      request.onerror = () => {
        console.warn(`Failed to save passport ${passportID}:`, request.error);
        reject(new Error(`Failed to save passport ${passportID}: ${request.error}`));
      };
    });
  }

  async getPassport(passportID: number): Promise<any> {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['passports'], 'readonly');
      const store = transaction.objectStore('passports');
      const request = store.get(passportID);

      request.onsuccess = () => {
        const result = request.result as CachedPassport | undefined;
        resolve(result ? result.data : null);
      };

      request.onerror = () => {
        console.warn(`Failed to get passport ${passportID}:`, request.error);
        reject(new Error(`Failed to get passport ${passportID}: ${request.error}`));
      };
    });
  }

  async getAllPassports(): Promise<any[]> {
    try {
      const db = await this.ensureDB();
      return new Promise((resolve, reject) => {
        try {
          const transaction = db.transaction(['passports'], 'readonly');
          const store = transaction.objectStore('passports');
          const request = store.getAll();

          request.onsuccess = () => {
            const results = request.result as CachedPassport[] | null | undefined;
            resolve(results && Array.isArray(results) ? results.map(r => r.data) : []);
          };

          request.onerror = () => {
            console.warn('Failed to get all passports:', request.error);
            // Return empty array instead of rejecting to prevent blocking
            resolve([]);
          };

          transaction.onerror = () => {
            console.warn('Transaction error getting all passports:', transaction.error);
            resolve([]);
          };
        } catch (error: any) {
          console.warn('Error accessing passports from IndexedDB:', error);
          resolve([]);
        }
      });
    } catch (error: any) {
      console.warn('Failed to get all passports, database may be closing:', error);
      return [];
    }
  }

  async saveAllPassports(passports: any[]): Promise<void> {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['passports'], 'readwrite');
      const store = transaction.objectStore('passports');
      
      let completed = 0;
      const total = passports.length;

      if (total === 0) {
        resolve();
        return;
      }

      for (const passport of passports) {
        const cachedPassport: CachedPassport = {
          passportID: passport.passportID || passport.id,
          data: passport,
          timestamp: Date.now()
        };

        const request = store.put(cachedPassport);
        
        request.onsuccess = () => {
          completed++;
          if (completed === total) {
            console.log(`Saved ${total} passports to IndexedDB`);
            resolve();
          }
        };

        request.onerror = () => {
          console.warn(`Failed to save passport:`, request.error);
          completed++;
          if (completed === total) {
            resolve(); // Resolve anyway to not block
          }
        };
      }
    });
  }

  // ==================== COMMENTS OPERATIONS ====================

  async saveComments(passportID: number, comments: any[]): Promise<void> {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['comments'], 'readwrite');
      const store = transaction.objectStore('comments');
      
      const cachedComments: CachedComments = {
        passportID,
        comments,
        timestamp: Date.now()
      };

      const request = store.put(cachedComments);

      request.onsuccess = () => {
        console.log(`Saved ${comments.length} comments for passport ${passportID}`);
        resolve();
      };

      request.onerror = () => {
        console.warn(`Failed to save comments for passport ${passportID}:`, request.error);
        reject(new Error(`Failed to save comments for passport ${passportID}: ${request.error}`));
      };
    });
  }

  async getComments(passportID: number): Promise<any[]> {
    return this.getCachedData(
      passportID,
      'comments',
      'comments',
      (result: CachedComments | undefined) => result ? result.comments : []
    );
  }

  /**
   * Helper method to get cached data with consistent error handling
   */
  private async getCachedData<T>(
    passportID: number,
    storeName: string,
    dataType: string,
    extractData: (result: T | undefined) => any[]
  ): Promise<any[]> {
    try {
      const db = await this.ensureDB();
      return new Promise((resolve) => {
        try {
          const transaction = db.transaction([storeName], 'readonly');
          const store = transaction.objectStore(storeName);
          const request = store.get(passportID);

          request.onsuccess = () => {
            const result = request.result as T | undefined;
            resolve(extractData(result));
          };

          request.onerror = () => {
            console.warn(`Failed to get ${dataType} for passport ${passportID}:`, request.error);
            // Return empty array instead of rejecting to prevent blocking
            resolve([]);
          };

          transaction.onerror = () => {
            console.warn(`Transaction error getting ${dataType} for passport ${passportID}:`, transaction.error);
            resolve([]);
          };
        } catch (error: any) {
          console.warn(`Error accessing ${dataType} for passport ${passportID}:`, error);
          resolve([]);
        }
      });
    } catch (error: any) {
      console.warn(`Failed to get ${dataType} for passport ${passportID}, database may be closing:`, error);
      return [];
    }
  }

  // ==================== HISTORY OPERATIONS ====================

  async saveHistory(passportID: number, history: any[]): Promise<void> {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['history'], 'readwrite');
      const store = transaction.objectStore('history');
      
      const cachedHistory: CachedHistory = {
        passportID,
        history,
        timestamp: Date.now()
      };

      const request = store.put(cachedHistory);

      request.onsuccess = () => {
        console.log(`Saved ${history.length} history entries for passport ${passportID}`);
        resolve();
      };

      request.onerror = () => {
        console.warn(`Failed to save history for passport ${passportID}:`, request.error);
        reject(new Error(`Failed to save history for passport ${passportID}: ${request.error}`));
      };
    });
  }

  async getHistory(passportID: number): Promise<any[]> {
    return this.getCachedData(
      passportID,
      'history',
      'history',
      (result: CachedHistory | undefined) => result ? result.history : []
    );
  }

  /**
   * Get all history entries from IndexedDB for all passports
   * Used for syncing offline history entries to backend
   */
  async getAllHistories(): Promise<CachedHistory[]> {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['history'], 'readonly');
      const store = transaction.objectStore('history');
      const request = store.getAll();

      request.onsuccess = () => {
        const allHistories = request.result as CachedHistory[];
        console.log(`📖 Retrieved ${allHistories.length} passport history records from IndexedDB`);
        resolve(allHistories);
      };

      request.onerror = () => {
        console.warn('Failed to get all histories:', request.error);
        reject(new Error(`Failed to get all histories: ${request.error}`));
      };
    });
  }

  // ==================== IMAGE OPERATIONS ====================
  // Image caching methods (saveImage, getImages, deleteImages) have been removed.
  // The 'images' object store definition is kept in onupgradeneeded to avoid breaking existing databases.

  // ==================== METADATA OPERATIONS ====================

  async setMetadata(key: string, value: any): Promise<void> {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['metadata'], 'readwrite');
      const store = transaction.objectStore('metadata');
      const request = store.put({ key, value, timestamp: Date.now() });

      request.onsuccess = () => resolve();
      request.onerror = () => reject(new Error(`Failed to set metadata: ${request.error}`));
    });
  }

  async getMetadata(key: string): Promise<any> {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['metadata'], 'readonly');
      const store = transaction.objectStore('metadata');
      const request = store.get(key);

      request.onsuccess = () => {
        const result = request.result;
        resolve(result ? result.value : null);
      };

      request.onerror = () => reject(new Error(`Failed to get metadata: ${request.error}`));
    });
  }

  // ==================== SHUNTING YARDS OPERATIONS ====================
  // Shunting yard caching methods (saveShuntingYards, getShuntingYards) have been removed.
  // The 'shuntingYards' object store definition is kept in onupgradeneeded to avoid breaking existing databases.

  // ==================== AUTHORISED USERS OPERATIONS ====================

  async saveAuthorisedUsers(userId: number, authorisedUsers: any[]): Promise<void> {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['authorisedUsers'], 'readwrite');
      const store = transaction.objectStore('authorisedUsers');

      const data: CachedAuthorisedUsers = {
        userId,
        authorisedUsers,
        timestamp: Date.now()
      };

      const request = store.put(data);

      request.onsuccess = () => {
        console.log(`Saved authorised users for user ${userId}`);
        resolve();
      };

      request.onerror = () => {
        console.warn('Failed to save authorised users:', request.error);
        reject(new Error(`Failed to save authorised users: ${request.error}`));
      };
    });
  }

  async getAuthorisedUsers(userId: number): Promise<any[] | null> {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['authorisedUsers'], 'readonly');
      const store = transaction.objectStore('authorisedUsers');
      const request = store.get(userId);

      request.onsuccess = () => {
        const result = request.result as CachedAuthorisedUsers | undefined;
        resolve(result ? result.authorisedUsers : null);
      };

      request.onerror = () => reject(new Error(`Failed to get authorised users: ${request.error}`));
    });
  }

  // ==================== ORIGINS OPERATIONS ====================

  async saveOrigins(roleId: number, passportId: string | undefined, origins: any[]): Promise<void> {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['origins'], 'readwrite');
      const store = transaction.objectStore('origins');

      // Create composite key: roleId-passportId (or just roleId if no passportId)
      const key = passportId ? `${roleId}-${passportId}` : `${roleId}`;

      const data: CachedOrigins = {
        key,
        roleId,
        passportId: passportId || undefined,
        origins,
        timestamp: Date.now()
      };

      const request = store.put(data);

      request.onsuccess = () => {
        const passportIdText = passportId ? `, passportId ${passportId}` : '';
        console.log(`Saved origins for roleId ${roleId}${passportIdText}`);
        resolve();
      };

      request.onerror = () => {
        console.warn('Failed to save origins:', request.error);
        reject(new Error(`Failed to save origins: ${request.error}`));
      };
    });
  }

  async getOrigins(roleId: number, passportId: string | undefined): Promise<any[] | null> {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['origins'], 'readonly');
      const store = transaction.objectStore('origins');
      
      // Create composite key: roleId-passportId (or just roleId if no passportId)
      const key = passportId ? `${roleId}-${passportId}` : `${roleId}`;
      
      const request = store.get(key);

      request.onsuccess = () => {
        const result = request.result as CachedOrigins | undefined;
        resolve(result ? result.origins : null);
      };

      request.onerror = () => reject(new Error(`Failed to get origins: ${request.error}`));
    });
  }

  // ==================== COMMENT NOTIFICATIONS OPERATIONS ====================

  async saveCommentNotifications(userId: number, notifications: any[]): Promise<void> {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['commentNotifications'], 'readwrite');
      const store = transaction.objectStore('commentNotifications');

      const data: CachedCommentNotifications = {
        userId,
        notifications,
        timestamp: Date.now()
      };

      const request = store.put(data);

      request.onsuccess = () => {
        console.log(`Saved ${notifications.length} comment notifications for user ${userId}`);
        resolve();
      };

      request.onerror = () => {
        console.warn('Failed to save comment notifications:', request.error);
        reject(new Error(`Failed to save comment notifications: ${request.error}`));
      };
    });
  }

  async getCommentNotifications(userId: number): Promise<any[] | null> {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['commentNotifications'], 'readonly');
      const store = transaction.objectStore('commentNotifications');
      const request = store.get(userId);

      request.onsuccess = () => {
        const result = request.result as CachedCommentNotifications | undefined;
        if (result) {
          const age = Date.now() - result.timestamp;
          const maxAge = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

          if (age < maxAge) {
            console.log(`Retrieved ${result.notifications.length} cached comment notifications for user ${userId}`);
            resolve(result.notifications);
          } else if (navigator.onLine) {
            // Data is stale and we're online — return null to trigger a fresh fetch
            console.log(`Cached comment notifications for user ${userId} are stale (${Math.round(age / 3600000)}h old)`);
            resolve(null);
          } else {
            // When offline, return stale data rather than nothing
            console.log(`Returning stale comment notifications for user ${userId} (${Math.round(age / 3600000)}h old) -- device is offline`);
            resolve(result.notifications);
          }
        } else {
          resolve(null);
        }
      };

      request.onerror = () => {
        console.warn('Failed to get comment notifications:', request.error);
        reject(new Error(`Failed to get comment notifications: ${request.error}`));
      };
    });
  }

  async deleteCommentNotifications(userId: number): Promise<void> {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['commentNotifications'], 'readwrite');
      const store = transaction.objectStore('commentNotifications');
      const request = store.delete(userId);

      request.onsuccess = () => {
        console.log(`Deleted comment notifications for user ${userId}`);
        resolve();
      };

      request.onerror = () => {
        console.warn('Failed to delete comment notifications:', request.error);
        reject(new Error(`Failed to delete comment notifications: ${request.error}`));
      };
    });
  }

  // ==================== DELETE OPERATIONS ====================

  /**
   * Generic helper method to delete a record from an object store by key
   * @param storeName Name of the object store
   * @param key Key of the record to delete
   * @param displayName Display name for logging (e.g., "passport", "comments", "history")
   */
  private async deleteFromStore(storeName: string, key: number, displayName: string): Promise<void> {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.delete(key);

      request.onsuccess = () => {
        console.log(`Deleted ${displayName} for passport ${key}`);
        resolve();
      };

      request.onerror = () => {
        console.warn(`Failed to delete ${displayName} for passport ${key}:`, request.error);
        reject(new Error(`Failed to delete ${displayName} for passport ${key}: ${request.error}`));
      };
    });
  }

  async deletePassport(passportID: number): Promise<void> {
    return this.deleteFromStore('passports', passportID, 'passport');
  }

  async deleteComments(passportID: number): Promise<void> {
    return this.deleteFromStore('comments', passportID, 'comments');
  }

  async deleteHistory(passportID: number): Promise<void> {
    return this.deleteFromStore('history', passportID, 'history');
  }

  async deleteAllDataForPassport(passportID: number): Promise<void> {
    console.log(`Deleting all cached data for passport ${passportID}`);
    
    try {
      await Promise.all([
        this.deletePassport(passportID),
        this.deleteComments(passportID),
        this.deleteHistory(passportID)
      ]);
      console.log(`✅ All cached data deleted for passport ${passportID}`);
    } catch (error) {
      console.warn(`❌ Error deleting cached data for passport ${passportID}:`, error);
      throw error;
    }
  }

  // ==================== UTILITY OPERATIONS ====================

  async clearAll(): Promise<void> {
    try {
      await this.clearStoresInSequence(this.allStoreNames);
    } catch {
      await this.deleteDatabase();
    }
  }

  /**
   * Clear persona-specific cached data when switching personas.
   * Preserves: drafts, outgoingPassports, metadata, commentNotifications
   * Clears: passports, comments, history, origins, authorisedUsers
   */
  async clearPersonaData(): Promise<void> {
    await this.clearStoresInSequence(this.personaStoreNames, { continueOnError: true });
    console.log('Cleared persona-specific IndexedDB stores');
  }

  async getStorageInfo(): Promise<{ passports: number; comments: number; history: number; drafts: number; outgoing: number }> {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['passports', 'comments', 'history', 'drafts', 'outgoingPassports'], 'readonly');
      
      const info = { passports: 0, comments: 0, history: 0, drafts: 0, outgoing: 0 };
      let completed = 0;
      const totalStores = 5;

      const countStore = (storeName: 'passports' | 'comments' | 'history' | 'drafts' | 'outgoingPassports', infoKey: keyof typeof info) => {
        const store = transaction.objectStore(storeName);
        const request = store.count();
        
        request.onsuccess = () => {
          info[infoKey] = request.result;
          completed++;
          if (completed === totalStores) {
            resolve(info);
          }
        };

        request.onerror = () => {
          completed++;
          if (completed === totalStores) {
            resolve(info);
          }
        };
      };

      countStore('passports', 'passports');
      countStore('comments', 'comments');
      countStore('history', 'history');
      countStore('drafts', 'drafts');
      countStore('outgoingPassports', 'outgoing');
    });
  }

  /**
   * Save a draft to IndexedDB
   * Converts File objects to Blobs for storage
   */
  async saveDraft(passportId: string, data: any): Promise<void> {
    try {
      const db = await this.ensureDB();
      
      // Convert File objects to Blobs for storage
      const dataWithBlobs = this.convertFilesToBlobs(data);
      
      const draft: CachedDraft = {
        passportId,
        data: dataWithBlobs,
        timestamp: Date.now()
      };

      return new Promise((resolve, reject) => {
        const transaction = db.transaction(['drafts'], 'readwrite');
        const store = transaction.objectStore('drafts');
        const request = store.put(draft);

        request.onsuccess = () => {
          console.log(`✅ Draft saved for passport ${passportId}`);
          resolve();
        };

        request.onerror = () => {
          console.warn('Failed to save draft:', request.error);
          reject(new Error(`Failed to save draft: ${request.error}`));
        };
      });
    } catch (error) {
      console.warn('Error saving draft:', error);
      throw error;
    }
  }

  /**
   * Get a draft from IndexedDB
   * Converts Blobs back to File objects
   */
  async getDraft(passportId: string): Promise<unknown> {
    try {
      const db = await this.ensureDB();
      
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(['drafts'], 'readonly');
        const store = transaction.objectStore('drafts');
        const request = store.get(passportId);

        request.onsuccess = () => {
          const result = request.result as CachedDraft | undefined;
          if (!result) {
            resolve(null);
            return;
          }

          // Convert Blobs back to File objects
          const dataWithFiles = this.convertBlobsToFiles(result.data);
          resolve(dataWithFiles);
        };

        request.onerror = () => {
          console.warn('Failed to get draft:', request.error);
          reject(new Error(`Failed to get draft: ${request.error}`));
        };
      });
    } catch (error) {
      console.warn('Error getting draft:', error);
      return null;
    }
  }

  /**
   * Delete a draft from IndexedDB
   */
  async deleteDraft(passportId: string): Promise<void> {
    return this.deleteFromStoreByStringKey(passportId, 'drafts', 'Draft');
  }

  /**
   * Get all drafts from IndexedDB
   */
  async getAllDrafts(): Promise<CachedDraft[]> {
    try {
      const db = await this.ensureDB();
      
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(['drafts'], 'readonly');
        const store = transaction.objectStore('drafts');
        const request = store.getAll();

        request.onsuccess = () => {
          const results = request.result;
          resolve(results && Array.isArray(results) ? results : []);
        };

        request.onerror = () => {
          console.warn('Failed to get all drafts:', request.error);
          reject(new Error(`Failed to get all drafts: ${request.error}`));
        };
      });
    } catch (error) {
      console.warn('Error getting all drafts:', error);
      return [];
    }
  }

  /**
   * Helper method to delete data from a store with string key and consistent error handling
   */
  private async deleteFromStoreByStringKey(passportId: string, storeName: string, itemName: string): Promise<void> {
    try {
      const db = await this.ensureDB();
      
      return new Promise((resolve, reject) => {
        const transaction = db.transaction([storeName], 'readwrite');
        const store = transaction.objectStore(storeName);
        const request = store.delete(passportId);

        request.onsuccess = () => {
          console.log(`✅ ${itemName} deleted for passport ${passportId}`);
          resolve();
        };

        request.onerror = () => {
          console.warn(`Failed to delete ${itemName.toLowerCase()}:`, request.error);
          reject(new Error(`Failed to delete ${itemName.toLowerCase()}: ${request.error}`));
        };
      });
    } catch (error) {
      console.warn(`Error deleting ${itemName.toLowerCase()}:`, error);
      throw error;
    }
  }

  /**
   * Save an outgoing passport (submitted offline, waiting for sync)
   * Converts File objects to Blobs for storage (same as drafts — required on iOS Safari).
   */
  async saveOutgoingPassport(passportId: string, data: any): Promise<void> {
    try {
      const db = await this.ensureDB();
      const dataWithBlobs = this.convertFilesToBlobs(data);

      return new Promise((resolve, reject) => {
        const transaction = db.transaction(['outgoingPassports'], 'readwrite');
        const store = transaction.objectStore('outgoingPassports');
        
        const outgoingPassport: CachedOutgoingPassport = {
          passportId,
          data: dataWithBlobs,
          timestamp: Date.now()
        };
        
        const request = store.put(outgoingPassport);

        request.onsuccess = () => {
          console.log(`✅ Outgoing passport saved for passport ${passportId}`);
          resolve();
        };

        request.onerror = () => {
          console.warn('Failed to save outgoing passport:', request.error);
          reject(new Error(`Failed to save outgoing passport: ${request.error}`));
        };
      });
    } catch (error) {
      console.warn('Error saving outgoing passport:', error);
      throw error;
    }
  }

  /**
   * Get an outgoing passport by ID
   */
  async getOutgoingPassport(passportId: string): Promise<unknown> {
    try {
      const db = await this.ensureDB();
      
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(['outgoingPassports'], 'readonly');
        const store = transaction.objectStore('outgoingPassports');
        const request = store.get(passportId);

        request.onsuccess = () => {
          const result = request.result as CachedOutgoingPassport | undefined;
          if (!result) {
            resolve(null);
            return;
          }
          resolve(this.convertBlobsToFiles(result.data));
        };

        request.onerror = () => {
          console.warn('Failed to get outgoing passport:', request.error);
          reject(new Error(`Failed to get outgoing passport: ${request.error}`));
        };
      });
    } catch (error) {
      console.warn('Error getting outgoing passport:', error);
      return null;
    }
  }

  /**
   * Get all outgoing passports
   */
  async getAllOutgoingPassports(): Promise<CachedOutgoingPassport[]> {
    try {
      const db = await this.ensureDB();
      
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(['outgoingPassports'], 'readonly');
        const store = transaction.objectStore('outgoingPassports');
        const request = store.getAll();

        request.onsuccess = () => {
          const results = request.result;
          resolve(results && Array.isArray(results) ? results : []);
        };

        request.onerror = () => {
          console.warn('Failed to get all outgoing passports:', request.error);
          reject(new Error(`Failed to get all outgoing passports: ${request.error}`));
        };
      });
    } catch (error) {
      console.warn('Error getting all outgoing passports:', error);
      return [];
    }
  }

  /**
   * Delete an outgoing passport (after successful sync)
   */
  async deleteOutgoingPassport(passportId: string): Promise<void> {
    return this.deleteFromStoreByStringKey(passportId, 'outgoingPassports', 'Outgoing passport');
  }

  /**
   * Convert File objects to Blobs for IndexedDB storage
   */
  private convertFilesToBlobs(data: any): any {
    if (!data || typeof data !== 'object') {
      return data;
    }

    if (data instanceof File) {
      // Convert File to Blob
      return new Blob([data], { type: data.type });
    }

    if (Array.isArray(data)) {
      return data.map(item => this.convertFilesToBlobs(item));
    }

    const result: any = {};
    for (const [key, value] of Object.entries(data)) {
      if (value instanceof File) {
        // Store File metadata along with Blob
        result[key] = {
          _blob: new Blob([value], { type: value.type }),
          _fileName: value.name,
          _lastModified: value.lastModified,
          _isFile: true
        };
      } else if (Array.isArray(value) && value.some(item => item instanceof File)) {
        // Handle array of Files
        result[key] = value.map((item: any) => {
          if (item instanceof File) {
            return {
              _blob: new Blob([item], { type: item.type }),
              _fileName: item.name,
              _lastModified: item.lastModified,
              _isFile: true
            };
          }
          return item;
        });
      } else if (value && typeof value === 'object') {
        result[key] = this.convertFilesToBlobs(value);
      } else {
        result[key] = value;
      }
    }

    return result;
  }

  /**
   * Convert Blobs back to File objects when loading from IndexedDB
   */
  convertBlobsToFiles(data: any): any {
    if (!data || typeof data !== 'object') {
      return data;
    }

    if (data._isFile && data._blob instanceof Blob) {
      return this.createFileFromBlob(data);
    }

    if (Array.isArray(data)) {
      return data.map(item => this.convertBlobsToFiles(item));
    }

    return this.convertObjectBlobsToFiles(data);
  }

  /**
   * Create a File object from a Blob representation
   */
  private createFileFromBlob(fileData: { _blob: Blob; _fileName?: string; _lastModified?: number }): File {
    return new File([fileData._blob], fileData._fileName || 'file', {
      type: fileData._blob.type,
      lastModified: fileData._lastModified || Date.now()
    });
  }

  /**
   * Convert object properties containing Blobs to Files
   */
  private convertObjectBlobsToFiles(data: Record<string, any>): any {
    const result: any = {};
    for (const [key, value] of Object.entries(data)) {
      result[key] = this.convertValueToFiles(value);
    }
    return result;
  }

  /**
   * Convert a single value (which may contain File representations) to Files
   */
  private convertValueToFiles(value: any): any {
    if (value && typeof value === 'object' && value._isFile && value._blob instanceof Blob) {
      return this.createFileFromBlob(value);
    }
    
    if (Array.isArray(value) && value.length > 0) {
      return this.convertArrayToFiles(value);
    }
    
    if (value && typeof value === 'object') {
      return this.convertBlobsToFiles(value);
    }
    
    return value;
  }

  /**
   * Convert an array that may contain File representations
   */
  private convertArrayToFiles(value: any[]): any[] {
    const firstItem = value[0];
    if (firstItem && typeof firstItem === 'object' && '_isFile' in firstItem && firstItem._isFile) {
      return value.map((item: any) => {
        if (item._isFile && item._blob instanceof Blob) {
          return this.createFileFromBlob(item);
        }
        return this.convertBlobsToFiles(item);
      });
    }
    return value;
  }

  // ==================== MERGE / DELTA HELPERS ====================

  /**
   * Get the cached timestamp for comments without loading the full array.
   * Returns epoch ms or null if no cache exists.
   */
  async getCommentsTimestamp(passportID: number): Promise<number | null> {
    try {
      const db = await this.ensureDB();
      return new Promise((resolve) => {
        try {
          const transaction = db.transaction(['comments'], 'readonly');
          const store = transaction.objectStore('comments');
          const request = store.get(passportID);

          request.onsuccess = () => {
            const result = request.result as CachedComments | undefined;
            resolve(result ? result.timestamp : null);
          };

          request.onerror = () => {
            console.warn(`Failed to get comments timestamp for passport ${passportID}`);
            resolve(null);
          };

          transaction.onerror = () => resolve(null);
        } catch {
          resolve(null);
        }
      });
    } catch {
      return null;
    }
  }

  /**
   * Get the cached timestamp for history without loading the full array.
   * Returns epoch ms or null if no cache exists.
   */
  async getHistoryTimestamp(passportID: number): Promise<number | null> {
    try {
      const db = await this.ensureDB();
      return new Promise((resolve) => {
        try {
          const transaction = db.transaction(['history'], 'readonly');
          const store = transaction.objectStore('history');
          const request = store.get(passportID);

          request.onsuccess = () => {
            const result = request.result as CachedHistory | undefined;
            resolve(result ? result.timestamp : null);
          };

          request.onerror = () => {
            console.warn(`Failed to get history timestamp for passport ${passportID}`);
            resolve(null);
          };

          transaction.onerror = () => resolve(null);
        } catch {
          resolve(null);
        }
      });
    } catch {
      return null;
    }
  }

  /**
   * Get the cached timestamp for comment notifications without loading the full array.
   * Returns epoch ms or null if no cache exists.
   */
  async getCommentNotificationsTimestamp(userId: number): Promise<number | null> {
    try {
      const db = await this.ensureDB();
      return new Promise((resolve) => {
        try {
          const transaction = db.transaction(['commentNotifications'], 'readonly');
          const store = transaction.objectStore('commentNotifications');
          const request = store.get(userId);

          request.onsuccess = () => {
            const result = request.result as CachedCommentNotifications | undefined;
            resolve(result ? result.timestamp : null);
          };

          request.onerror = () => {
            console.warn(`Failed to get comment notifications timestamp for user ${userId}`);
            resolve(null);
          };

          transaction.onerror = () => resolve(null);
        } catch {
          resolve(null);
        }
      });
    } catch {
      return null;
    }
  }

  /**
   * Merge new comments into existing cached comments, deduplicating by commentID.
   * Updates the timestamp to now.
   */
  async mergeComments(passportID: number, newComments: any[]): Promise<void> {
    try {
      const existing = await this.getComments(passportID);
      const merged = this.deduplicateById(existing, newComments, 'commentID');
      await this.saveComments(passportID, merged);
      console.log(`Merged comments for passport ${passportID}: ${existing.length} existing + ${newComments.length} new = ${merged.length} total`);
    } catch (error) {
      console.warn(`Failed to merge comments for passport ${passportID}:`, error);
      throw error;
    }
  }

  /**
   * Merge new history entries into existing cached history, deduplicating by historyID.
   * Updates the timestamp to now.
   */
  async mergeHistory(passportID: number, newHistory: any[]): Promise<void> {
    try {
      const existing = await this.getHistory(passportID);
      const merged = this.deduplicateById(existing, newHistory, 'historyID');
      await this.saveHistory(passportID, merged);
      console.log(`Merged history for passport ${passportID}: ${existing.length} existing + ${newHistory.length} new = ${merged.length} total`);
    } catch (error) {
      console.warn(`Failed to merge history for passport ${passportID}:`, error);
      throw error;
    }
  }

  /**
   * Merge new comment notifications into existing cached notifications, deduplicating by commentID.
   * Updates the timestamp to now.
   */
  async mergeCommentNotifications(userId: number, newNotifications: any[]): Promise<void> {
    try {
      // Load existing without staleness check -- we want the raw data for merging
      const db = await this.ensureDB();
      const existing: any[] = await new Promise((resolve) => {
        try {
          const transaction = db.transaction(['commentNotifications'], 'readonly');
          const store = transaction.objectStore('commentNotifications');
          const request = store.get(userId);

          request.onsuccess = () => {
            const result = request.result as CachedCommentNotifications | undefined;
            resolve(result ? result.notifications : []);
          };

          request.onerror = () => resolve([]);
          transaction.onerror = () => resolve([]);
        } catch {
          resolve([]);
        }
      });

      const merged = this.deduplicateById(existing, newNotifications, 'commentID');
      await this.saveCommentNotifications(userId, merged);
      console.log(`Merged comment notifications for user ${userId}: ${existing.length} existing + ${newNotifications.length} new = ${merged.length} total`);
    } catch (error) {
      console.warn(`Failed to merge comment notifications for user ${userId}:`, error);
      throw error;
    }
  }

  /**
   * Deduplicate two arrays by a given ID field.
   * New items overwrite existing items with the same ID.
   */
  private deduplicateById(existing: any[], incoming: any[], idField: string): any[] {
    const map = new Map<any, any>();
    for (const item of existing) {
      const key = item[idField];
      if (key != null) {
        map.set(key, item);
      }
    }
    for (const item of incoming) {
      const key = item[idField];
      if (key != null) {
        map.set(key, item);
      }
    }
    return Array.from(map.values());
  }

  // ==================== DEBUG HELPERS ====================

  /**
   * Remove passport entries from IndexedDB that are not in the API response
   * Preserves outgoing records (user's pending work)
   * @param validPassportIds - Array of passport IDs that are valid from the latest API response
   * @returns Number of stale passports removed
   */
  async cleanupStalePassports(validPassportIds: string[]): Promise<number> {
    try {
      const validIdSet = new Set(validPassportIds);
      
      // Get all cached passports
      const allCached = await this.getAllPassports();
      const outgoing = await this.getAllOutgoingPassports();
      const outgoingIds = new Set(outgoing.map(o => o.passportId));
      
      let removedCount = 0;
      
      for (const cached of allCached) {
        const id = cached.passportID.toString();
        // Don't remove if: still valid from API OR is an outgoing record
        if (!validIdSet.has(id) && !outgoingIds.has(id)) {
          try {
            await this.deleteAllDataForPassport(Number(id));
            removedCount++;
            console.log(`🗑️ Removed stale passport ${id} from IndexedDB`);
          } catch (e) {
            console.warn(`Failed to delete stale passport ${id}:`, e);
          }
        }
      }
      
      if (removedCount > 0) {
        console.log(`✅ Cleaned up ${removedCount} stale passports from IndexedDB`);
      }
      return removedCount;
    } catch (error) {
      console.warn('Failed to cleanup stale passports:', error);
      return 0;
    }
  }
}

